// ENG-4787: defensive guards for `.mcp.json` writes by the manager and
// provisioning writers. ENG-4744 forced an operator workaround
// (chattr +i on the agent's .mcp.json) because the manager's 10s
// integration-sync tick was overwriting the file with a broken
// cloud-broker entry — literal `${AGT_API_KEY}` placeholders and a
// missing `AGT_AGENT_ID`. The underlying writer bug was fixed in
// ENG-4739, but the manager had no guardrails against the *next*
// writer regression.
//
// This module provides two layers:
//   1. `validateRenderedMcpConfig` — a pure function that catches
//      unexpanded ${...} placeholders, missing required env keys per
//      MCP server, and JSON-shape failures. Cheap to call before any
//      write.
//   2. `safeWriteJsonAtomic` — a writer that does a temp-file +
//      rename dance with a single .bak snapshot. Recovery from a bad
//      write becomes `mv file.bak file` instead of an SSH session.
//
// Both are pure / fs-isolated so the rest of the provisioning code
// can be tested without an integration-tick fixture.

import {
  chmodSync,
  existsSync,
  readFileSync,
  renameSync,
  writeFileSync,
  unlinkSync,
} from 'node:fs';

// ENG-5901 (ADR-0018 Phase 1): `.mcp.json` carries `${VAR}` placeholders
// whose raw values live in `.env.integrations` (mode 0600). The file
// itself should be owner-only too — symmetric with `.env.integrations`
// and a defence-in-depth step against a co-located reader. Audit run
// 2026-06-02 found it world-readable (0644).
export const MCP_FILE_MODE = 0o600;

// ENG-5901 Track D: value-shape lint for literal secrets. Type-only
// imports flow the OTHER way (mcp-secret-lint imports McpConfig types
// from here), so this value import creates no runtime cycle.
import {
  scanConfigForLiteralSecrets,
  formatLiteralSecretRejection,
} from './mcp-secret-lint.js';

// ENG-5901 PR 3: last logged rejection fingerprint per .mcp.json path —
// the dedupe memory for the armed lint's stderr lines (see
// safeWriteMcpJson). Process-lifetime state is intentional: the manager
// is long-lived and the goal is exactly to stop per-tick repeats.
const lastRejectionFingerprintByPath = new Map<string, string>();

// ── Validator ─────────────────────────────────────────────────────────────

export interface McpServerEntry {
  command?: string;
  args?: string[];
  env?: Record<string, string>;
  // ENG-4806/etc forward-compat — http transports etc. live alongside
  // command-based stdio. Validator ignores fields it doesn't recognise.
  type?: string;
  url?: string;
  headers?: Record<string, string>;
}

export interface McpConfig {
  mcpServers?: Record<string, McpServerEntry>;
}

export type McpValidationFailure =
  | 'unexpanded_placeholder'
  | 'missing_required_env'
  | 'invalid_json_shape'
  // ENG-5074: URL-based entries (Streamable HTTP / SSE remote MCPs)
  // require a `type` field per Claude Code's MCP schema. Pre-fix the
  // writer omitted it and the sanitizer's preserve-headers path also
  // didn't add one, leading to claude rejecting the config at startup
  // and the agent's tmux session looping forever. Surface the missing
  // field at write time so the bug can't ship to the host again.
  | 'remote_mcp_missing_type'
  // ENG-5901 Track D: a literal credential shape (xoxb-/xapp-/tlk_/ak_/
  // Telegram/JWT) in an env value or HTTP header value. Every secret in
  // .mcp.json must be a `${VAR}` template with the raw value in
  // .env.integrations; a literal here is a writer regression and the
  // write is rejected (previous good file stays in place).
  | 'literal_secret';

export interface McpValidationError {
  kind: McpValidationFailure;
  /** Server key in `mcpServers` (e.g. 'cloud-broker'); '*' if structural. */
  server: string;
  /** Human-readable message safe to log. */
  message: string;
}

export type McpValidationResult =
  | { ok: true }
  | { ok: false; errors: McpValidationError[] };

/**
 * Required env keys per known MCP server with their substitution
 * contract:
 *
 *   mustBeConcrete: true   — value is baked at provision time (e.g.
 *     AGT_AGENT_ID is the agent's literal UUID); a `${...}`
 *     placeholder here is the writer-regression shape ENG-4744 hit.
 *   mustBeConcrete: false  — value is intentionally a `${VAR}`
 *     placeholder that Claude / the manager substitutes at MCP-
 *     launch time (AGT_HOST, AGT_API_KEY follow this pattern; see
 *     packages/core/src/provisioning/frameworks/claudecode/index.ts
 *     ~line 1217).
 *
 * Keep the list small — only servers where a missing or wrongly-shaped
 * key produces broken runtime behaviour.
 */
interface RequiredEnvRule {
  key: string;
  mustBeConcrete: boolean;
}

const REQUIRED_ENV_RULES_BY_SERVER: Readonly<Record<string, readonly RequiredEnvRule[]>> = {
  'cloud-broker': [
    { key: 'AGT_HOST', mustBeConcrete: false },
    { key: 'AGT_API_KEY', mustBeConcrete: false },
    // ENG-4744: this is the bug shape — writer used to omit this
    // entirely, or render it as a literal `${AGT_AGENT_ID}` instead
    // of the agent's real UUID. The broker has no way to substitute
    // it post-spawn, so a placeholder here = silently broken agent.
    { key: 'AGT_AGENT_ID', mustBeConcrete: true },
  ],
};

const PLACEHOLDER_RE = /\$\{[^}]+\}/;

/**
 * Validate a rendered `.mcp.json` config object before it's written.
 *
 * Catches the three failure modes that produced ENG-4744 and similar:
 *   - Unexpanded `${VAR}` placeholders left in env values (writer bug
 *     where the env var wasn't substituted before serialising).
 *   - Missing required env keys for known MCP servers.
 *   - Invalid JSON shape (mcpServers is not an object).
 *
 * Returns structured errors so callers can pick a single-line log
 * message; never throws.
 */
export function validateRenderedMcpConfig(config: unknown): McpValidationResult {
  const errors: McpValidationError[] = [];

  if (typeof config !== 'object' || config === null || Array.isArray(config)) {
    // CodeRabbit ENG-4787: arrays pass `typeof === 'object'`, so
    // without the Array.isArray guard `[]` would slip through and
    // then hit the `root.mcpServers === undefined` early return
    // (arrays don't have an `mcpServers` key) — accepting an array
    // root as a valid config.
    return {
      ok: false,
      errors: [
        {
          kind: 'invalid_json_shape',
          server: '*',
          message: 'config root must be a non-null object',
        },
      ],
    };
  }

  const root = config as { mcpServers?: unknown };
  if (root.mcpServers === undefined) {
    // Empty config (no servers) is valid — agents legitimately ship
    // without any MCP servers configured.
    return { ok: true };
  }
  if (typeof root.mcpServers !== 'object' || root.mcpServers === null) {
    return {
      ok: false,
      errors: [
        {
          kind: 'invalid_json_shape',
          server: '*',
          message: 'mcpServers must be an object',
        },
      ],
    };
  }

  if (Array.isArray(root.mcpServers)) {
    return {
      ok: false,
      errors: [
        {
          kind: 'invalid_json_shape',
          server: '*',
          message: 'mcpServers must be an object',
        },
      ],
    };
  }

  for (const [serverKey, raw] of Object.entries(root.mcpServers)) {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
      errors.push({
        kind: 'invalid_json_shape',
        server: serverKey,
        message: `entry must be an object`,
      });
      continue;
    }
    const entry = raw as McpServerEntry;

    // ENG-5074: URL-based remote MCPs require a `type` field per Claude
    // Code's MCP schema ('http' for Streamable HTTP, 'sse' for legacy
    // Server-Sent Events). The writer + sanitizer were both shipping
    // url+headers without a type, and claude rejected the whole config
    // at startup. Catch it here at write time instead of letting it
    // ride to the host. Stdio entries (command-based, no url) skip
    // this check entirely.
    const entryRecord = entry as Record<string, unknown>;
    if (typeof entryRecord['url'] === 'string') {
      const type = entryRecord['type'];
      if (type !== 'http' && type !== 'sse') {
        errors.push({
          kind: 'remote_mcp_missing_type',
          server: serverKey,
          message: `url-based entry must include \`type: "http"\` or \`type: "sse"\` (Claude Code MCP schema requires it)`,
        });
      }
    }

    // Required env keys for known servers per their substitution
    // contract (see REQUIRED_ENV_RULES_BY_SERVER). For mustBeConcrete
    // keys, a `${...}` placeholder is the ENG-4744 writer-regression
    // shape and we reject. For non-concrete keys, a placeholder is
    // legitimate (Claude / manager substitutes at exec time).
    const rules = REQUIRED_ENV_RULES_BY_SERVER[serverKey];
    if (rules) {
      const env = (entry.env ?? {}) as Record<string, string>;
      for (const rule of rules) {
        const value = env[rule.key];
        if (typeof value !== 'string' || value.length === 0) {
          errors.push({
            kind: 'missing_required_env',
            server: serverKey,
            message: `missing required env key: ${rule.key}`,
          });
          continue;
        }
        if (rule.mustBeConcrete && PLACEHOLDER_RE.test(value)) {
          errors.push({
            kind: 'unexpanded_placeholder',
            server: serverKey,
            message: `env.${rule.key} contains an unexpanded \${...} placeholder; expected a concrete value`,
          });
        }
      }
    }

    // ENG-8800: AGT_AGENT_ID must be concrete on EVERY server that carries it,
    // not only `cloud-broker`.
    //
    // The per-server rules above cover cloud-broker alone, so for `augmented`,
    // `direct-chat`, `slack` and `augmented-admin` a `${AGT_AGENT_ID}`
    // placeholder was treated as legitimate — "Claude/manager substitutes at
    // exec time". That assumption is now known to be unsafe for THIS key
    // specifically, because of what it substitutes FROM.
    //
    // A placeholder here resolves against the pane's `process.env`, and ENG-8800
    // established that the pane inherits `AGT_AGENT_ID` from the shared tmux
    // server's global environment — i.e. from whichever agent started it.
    // Neither bug alone is catastrophic: ENG-8800 on its own poisons only the
    // shell lane (git/gh), and a placeholder on its own resolves correctly on a
    // healthy host. TOGETHER they hand all five MCP servers a NEIGHBOUR's
    // identity — cloud-broker minting real cloud credentials, and
    // augmented-admin running fleet reads, as somebody else.
    //
    // `AGT_API_KEY` is host-wide and proves nothing about which agent is
    // calling, so for these servers AGT_AGENT_ID *is* the identity. It is never
    // a value that should be resolved late.
    //
    // (Credit: compound interaction spotted by sherlock on ENG-8800.)
    const env = (entry.env ?? {}) as Record<string, string>;
    const agentId = env['AGT_AGENT_ID'];
    if (typeof agentId === 'string' && PLACEHOLDER_RE.test(agentId)) {
      const already = errors.some(
        (e) => e.server === serverKey && e.kind === 'unexpanded_placeholder',
      );
      if (!already) {
        errors.push({
          kind: 'unexpanded_placeholder',
          server: serverKey,
          message:
            'env.AGT_AGENT_ID contains an unexpanded ${...} placeholder; it must be concrete '
            + 'on every server (ENG-8800: a late substitution reads the pane env, which can '
            + "carry a neighbouring agent's id)",
        });
      }
    }
  }

  return errors.length === 0 ? { ok: true } : { ok: false, errors };
}

/** Format the error list into a single log line for `manager.log`. */
export function formatValidationErrors(errors: McpValidationError[]): string {
  return errors.map((e) => `${e.server}:${e.kind}=${e.message}`).join('; ');
}

// ── Atomic writer ─────────────────────────────────────────────────────────

export interface AtomicWriteOptions {
  /** When true (default) write a `<path>.bak` snapshot of the existing
   *  file before swapping in the new one. Disable for paths where the
   *  caller manages backups itself. */
  keepBackup?: boolean;
  /**
   * Override for the rename syscall, used by the rollback regression
   * test (vitest can't spy on `node:fs` ESM exports). Production
   * callers should leave this unset — it defaults to `node:fs`'s
   * synchronous `renameSync`.
   */
  renamer?: (src: string, dest: string) => void;
  /**
   * ENG-5901: when set, chmod the temp file to this mode *before* the
   * rename, so the installed file lands with the right permissions
   * atomically. If the chmod fails the whole write aborts and rolls
   * back — a secret file with the wrong mode must never ship.
   */
  mode?: number;
}

/**
 * Atomically write `content` to `path` via temp-file + rename. Mirrors
 * the pattern already used for `daily-session.json.bak*`:
 *
 *   1. Write to <path>.new (same directory, same volume → rename is
 *      guaranteed atomic on POSIX).
 *   2. Rename existing <path> → <path>.bak (overwriting any prior
 *      .bak — single-slot snapshot for cheap recovery).
 *   3. Rename <path>.new → <path>.
 *
 * If any step fails the partial files are cleaned up so a half-
 * written state can't survive on disk.
 */
export function safeWriteJsonAtomic(
  path: string,
  content: string,
  opts: AtomicWriteOptions = {},
): void {
  const keepBackup = opts.keepBackup !== false;
  const rename = opts.renamer ?? renameSync;
  const tmpPath = `${path}.new`;
  const bakPath = `${path}.bak`;
  // CodeRabbit follow-up: track whether we've already moved the
  // original aside so the catch path can roll back. Without this,
  // a failure between the two renames would leave `path` missing
  // entirely (original moved to .bak, new file never installed) —
  // worse than a stale write because consumers can't even read.
  let movedOriginalToBackup = false;

  try {
    writeFileSync(tmpPath, content);
    // ENG-5901: set the final mode on the temp file before it's renamed
    // into place, so the install is atomic w.r.t. permissions too.
    if (opts.mode !== undefined) {
      chmodSync(tmpPath, opts.mode);
    }
  } catch (err) {
    // Couldn't write (or chmod) the temp file — clean up any partial
    // temp and bail without touching the original.
    try {
      if (existsSync(tmpPath)) unlinkSync(tmpPath);
    } catch {
      /* best-effort */
    }
    throw err;
  }

  try {
    if (keepBackup && existsSync(path)) {
      // overwriting the prior .bak is intentional — single-slot.
      rename(path, bakPath);
      movedOriginalToBackup = true;
    }
    rename(tmpPath, path);
  } catch (err) {
    // Clean up the orphan temp file so we don't leave debris.
    try {
      if (existsSync(tmpPath)) unlinkSync(tmpPath);
    } catch {
      /* best-effort */
    }
    // Roll back to last-known-good when we already moved the
    // original aside but failed to install the new file. Best-
    // effort: if the rollback itself fails, .bak still has the
    // previous content for manual recovery. Uses the real
    // renameSync (not the injected `rename`) so a test that throws
    // on rename for fault injection can still rely on the rollback
    // path running.
    if (movedOriginalToBackup && !existsSync(path) && existsSync(bakPath)) {
      try {
        renameSync(bakPath, path);
      } catch {
        /* best-effort */
      }
    }
    throw err;
  }
}

/** Read the most recent `.bak` snapshot for a path, if any. */
export function readBackup(path: string): string | null {
  const bakPath = `${path}.bak`;
  if (!existsSync(bakPath)) return null;
  try {
    return readFileSync(bakPath, 'utf-8');
  } catch {
    return null;
  }
}

// ── Combined entry point ──────────────────────────────────────────────────

export interface SafeWriteMcpResult {
  written: boolean;
  /** Validation errors when written=false. Empty array on success. */
  errors: McpValidationError[];
}

/**
 * Validate `config` and atomically write to `path` if validation
 * passes. Returns `{ written: false, errors }` on failure so callers
 * can log a single structured line and skip rather than corrupting
 * the existing file.
 *
 * This is the recommended entry point for all `.mcp.json` writers in
 * the manager's integration-sync loop; the previous `writeFileSync`
 * pattern at every callsite is the regression surface ENG-4744 hit.
 */
export function safeWriteMcpJson(
  path: string,
  config: McpConfig | unknown,
): SafeWriteMcpResult {
  const validation = validateRenderedMcpConfig(config);
  if (!validation.ok) {
    return { written: false, errors: validation.errors };
  }
  // ENG-5901 Track D: literal-secret lint, armed in the same change that
  // converted every writer to `${VAR}` templates (arming it earlier would
  // have rejected the writers' own literal output). A match rejects the
  // write — the previous good file stays — and emits one structured,
  // secret-free stderr line per finding (the contributor-side gate: a
  // future copy-paste of the old literal pattern fails at provision time,
  // not in production).
  //
  // ENG-5901 PR 3: per-path log dedupe. A hot writer loop re-attempting
  // the same rejected write was producing ~50% of manager.log on
  // agt-aws-1 — keep rejecting every attempt, but only LOG when the
  // finding fingerprint for this path changes (and clear the memory on a
  // clean write so a relapse logs again).
  const secretFindings = scanConfigForLiteralSecrets(config);
  if (secretFindings.length > 0) {
    const fingerprint = secretFindings
      .map((f) => `${f.server}.${f.field}.${f.location}`)
      .sort()
      .join('|');
    if (lastRejectionFingerprintByPath.get(path) !== fingerprint) {
      lastRejectionFingerprintByPath.set(path, fingerprint);
      for (const f of secretFindings) {
        process.stderr.write(`${formatLiteralSecretRejection(f)}\n`);
      }
    }
    return {
      written: false,
      errors: secretFindings.map((f) => ({
        kind: 'literal_secret' as const,
        server: f.server,
        message: `literal secret in ${f.location} field '${f.field}' (pattern ${f.pattern}); expected a \${VAR} template`,
      })),
    };
  }
  lastRejectionFingerprintByPath.delete(path);
  // ENG-5901: `.mcp.json` is a secret-bearing file (even post-templating
  // it carries the `${VAR}` contract and historically held literals) —
  // install it owner-only, atomically.
  safeWriteJsonAtomic(path, JSON.stringify(config, null, 2), { mode: MCP_FILE_MODE });
  return { written: true, errors: [] };
}

// ── ENG-5901: provision ⇄ project mirror parity ────────────────────────────
//
// `syncMcpToProject` copies `provision/.mcp.json` to `project/.mcp.json`
// verbatim. If a future change ever transforms content on the way (or a
// writer mutates one mirror without the other), the two could diverge on
// a secret-bearing field — the exact drift class ENG-4793 lineage warns
// about. This pure helper compares the secret-bearing fields of two
// rendered configs so the sync chokepoint (and tests) can assert parity.

/** Field-name shapes that carry a secret (env keys / header names). */
const SECRET_FIELD_NAME_RE = /TOKEN|KEY|SECRET|BEARER|PASSWORD/i;

export interface McpMirrorMismatch {
  server: string;
  field: string;
  location: 'env' | 'header';
  reason: 'missing-in-project' | 'missing-in-provision' | 'value-diverges';
}

function collectSecretFields(
  config: unknown,
): Map<string, { location: 'env' | 'header'; value: string }> {
  const out = new Map<string, { location: 'env' | 'header'; value: string }>();
  if (typeof config !== 'object' || config === null) return out;
  const servers = (config as McpConfig).mcpServers;
  if (typeof servers !== 'object' || servers === null) return out;
  for (const [server, raw] of Object.entries(servers)) {
    if (typeof raw !== 'object' || raw === null) continue;
    const entry = raw as McpServerEntry;
    for (const [block, location] of [
      [entry.env, 'env'],
      [entry.headers, 'header'],
    ] as Array<[Record<string, unknown> | undefined, 'env' | 'header']>) {
      if (!block) continue;
      for (const [field, value] of Object.entries(block)) {
        if (typeof value !== 'string') continue;
        if (!SECRET_FIELD_NAME_RE.test(field)) continue;
        out.set(`${server}\u0000${field}`, { location, value });
      }
    }
  }
  return out;
}

/**
 * Compare the secret-bearing fields of the provision and project mirrors.
 * Returns an empty array when they agree. Pure — no I/O.
 */
export function mcpMirrorParityErrors(
  provision: unknown,
  project: unknown,
): McpMirrorMismatch[] {
  const a = collectSecretFields(provision);
  const b = collectSecretFields(project);
  const mismatches: McpMirrorMismatch[] = [];
  const keys = new Set<string>([...a.keys(), ...b.keys()]);
  for (const key of keys) {
    const [server, field] = key.split('\u0000') as [string, string];
    const pv = a.get(key);
    const pj = b.get(key);
    if (pv && !pj) {
      mismatches.push({ server, field, location: pv.location, reason: 'missing-in-project' });
    } else if (!pv && pj) {
      mismatches.push({ server, field, location: pj.location, reason: 'missing-in-provision' });
    } else if (pv && pj && pv.value !== pj.value) {
      mismatches.push({ server, field, location: pv.location, reason: 'value-diverges' });
    }
  }
  return mismatches;
}

/** Secret-free structured log line for a mirror mismatch. */
export function formatMirrorMismatch(m: McpMirrorMismatch): string {
  return `[mcp-mirror] [parity-violation] server=${m.server} field=${m.field} location=${m.location} reason=${m.reason}`;
}
